import React, { ReactNode, RefObject, useCallback, useEffect } from 'react';
import FormInput from '../../FormInput/FormInput';
import PreviewResultPopUp from './PreviewResultPopUp/PreviewResultPopUp';
import { Namespace } from '../../../../../../translation/namespace';
import usePopupKeyboardNavigation, { Selected } from './hooks/usePopupKeyboardNavigation';
import { IEntity } from '../../../../../../types/api/entities/IEntity';
import type { Section } from './types/Section';
import { useSonTranslation } from '../../../../../../translation/useSonTranslation';
import { useComboBoxLiveRegionMessage } from './hooks/useComboBoxLiveRegionMessage';

export type SelectionHandler<T> = (item: Selected<T>) => void;
export type ComboBoxProps = {
  id: string;
  labelTitle: string;
  name?: string;
  modifierInput?: string;
  modifierListBox?: string;
  inputInContainer?: boolean;
  labelHidden?: boolean;
  hideLabelAddition?: boolean;
  labelHelpText?: string;
  resetButton?: boolean;
  placeholder?: string;
  required?: boolean;
  maxLength?: number;
  accessKey?: string;
  validationMessage?: string;
  ariaAutoComplete?: 'inline' | 'list' | 'both';
  disabled?: boolean;
};

export type FormsComboBoxProps<TResults extends IEntity> = ComboBoxProps & {
  inputValue?: string;
  // Event Handler
  onSelection: SelectionHandler<TResults>;
  results: Section<TResults>[] | undefined;
  onInput: React.ChangeEventHandler<HTMLInputElement>;
  loading: boolean;
  resultsOpen: boolean;
  openResults: () => void;
  closeResults: () => void;
  formInputRef: RefObject<HTMLInputElement | null>;
  clearInput: () => void;
  error?: 'noData' | 'invalidSearchTerm';
  resetError: () => void;
};

export default function AbstractFormsComboBox<TResults extends IEntity>({
  results,
  accessKey,
  ariaAutoComplete,
  hideLabelAddition,
  id,
  inputInContainer,
  labelHelpText,
  labelHidden,
  labelTitle,
  maxLength,
  modifierInput,
  modifierListBox,
  name,
  placeholder,
  required,
  resetButton,
  children,
  onSelection,
  validationMessage,
  // from useInput
  inputValue,
  formInputRef,
  onInput,
  loading,
  resultsOpen,
  openResults,
  closeResults,
  clearInput,
  error,
  resetError,
  disabled,
}: FormsComboBoxProps<TResults> & {
  children: (item?: TResults) => ReactNode;
}) {
  const { t } = useSonTranslation(Namespace.FormsComboBox);

  const resetState = useCallback(() => {
    closeResults();
    clearInput();
    resetError();
  }, [closeResults, clearInput, resetError]);

  const allResults = results && results.flatMap(r => r.results);
  const allResultsWithoutLinks =
    allResults &&
    allResults.filter(result => result.id !== 'allContentResults').filter(result => result.id !== 'allUserResults');

  // if results were requested:
  const hasResults = !!allResultsWithoutLinks;

  const { onKeyDown, markedItem } = usePopupKeyboardNavigation(
    allResults || [],
    onSelection,
    closeResults,
    openResults,
    resetState,
    resultsOpen,
    inputValue,
  );

  useComboBoxLiveRegionMessage({
    allResults,
    loading,
    error,
    resultsOpen,
    inputValue,
  });

  useEffect(() => {
    if (hasResults || loading || error) {
      openResults();
    } else {
      closeResults();
    }
    // openResult, closeResult and t are wrapped in useCallback
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, hasResults, error]);

  // only close if blur is not on popup items -> required for scrollbar usage
  const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const role = event.relatedTarget?.getAttribute('role');
    if (role !== 'option' && role !== 'listbox') {
      closeResults();
    }
  };

  return (
    <div className="combobox">
      {/* This once was connected to input via aria-described by. As we focus the input after interactions, */}
      {/* we decided to remove connection, because of help text bloat. */}
      <p className="invisible">
        {t('helpMessage')}
      </p>
      {/* if used correctly and wisely, accesskeys are a11y enhancement */}
      {/* eslint-disable-next-line jsx-a11y/no-access-key */}
      <FormInput
        formInputRef={formInputRef}
        id={id}
        name={name}
        value={inputValue}
        ariaRequired={required}
        hideLabelAddition={hideLabelAddition}
        inputInContainer={inputInContainer}
        labelHidden={labelHidden}
        labelHelpText={labelHelpText}
        labelTitle={labelTitle}
        labelId={`combobox-label-${id}`}
        modifierInput={modifierInput}
        autoComplete="off"
        placeholder={placeholder || undefined}
        resetButton={resetButton}
        // Validation
        validationMessage={validationMessage}
        // Eventhandler
        onInput={onInput}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        // Accessibillity
        ariaRole="combobox"
        ariaControls={`combobox-listbox-${id}`}
        ariaAutoComplete={ariaAutoComplete}
        AbstractFormsComboBox
        ariaOwns={`combobox-listbox-${id}`}
        ariaHasPopup="listbox"
        ariaExpanded={resultsOpen}
        accessKey={accessKey}
        maxLength={maxLength}
        disabled={disabled}
      />
      <PreviewResultPopUp
        selectedItem={markedItem}
        id={id}
        hidden={!resultsOpen && !loading}
        loading={loading}
        sections={results || []}
        allResultsWithoutLinks={allResultsWithoutLinks}
        modifier={modifierListBox}
        formInputRef={formInputRef}
        error={error && t(`error.${error}`)}
        onSelection={onSelection}
        onBlur={closeResults}
      >
        {children}
      </PreviewResultPopUp>
    </div>
  );
}
